import React, { useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import {
    Badge,
    Button,
    RegionState,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCellProps,
    TableRow,
} from '@freelancelabs/components';

import Card from '../../components/Card';
import { OrganizationKind } from '../../graphql/__generated__/schema';
import { AccountModulesDocument } from '../../graphql/__generated__/types';
import { refusalCode } from '../../graphql/refusalCode';
import { formatDate } from '../users/display';
import AccountModuleDialog from './AccountModuleDialog';
import { ModuleDirection } from './moduleRefusal';

interface Props {
    accountId: string;
    accountName: string;
    /** Freelance.com is `internal`; every client account is not. */
    kind: OrganizationKind | null | undefined;
}

/** The module a dialog is currently acting on, and which way the gesture goes. */
type Acting = { module: { code: string; label: string }; direction: ModuleDirection };

/**
 * The modules of one account. A module opened on an account is a row of
 * `organization_module`, keyed by `organization_id`, so it belongs on the account sheet.
 *
 * The list is the whole referential and never shortens: an undelivered module (no
 * reading path in the portal) is shown disabled with its reason, not filtered out. The
 * two gestures are disabled differently on purpose: opening refuses `MODULE_NOT_DELIVERED`
 * on such a module, closing never does, so a row posted by hand stays erasable.
 */
const AccountModulesPanel = ({ accountId, accountName, kind }: Props) => {
    const [acting, setActing] = useState<Acting | null>(null);

    // Skipped on Freelance.com: the internal organisation carries no portal module.
    const internal = kind === OrganizationKind.Internal;
    const { data, loading, error } = useQuery(AccountModulesDocument, {
        variables: { organizationId: accountId },
        skip: internal,
    });

    if (internal) {
        return null;
    }

    // `accountModules` is nullable: null means "the API did not say", not "empty
    // referential" (which is impossible, the rows being seeded by migration). The two
    // are told apart below.
    const modules = data?.accountModules ?? [];
    const openCount = modules.filter((module) => module.enabled).length;
    // The states below key on the length, never on "did the field come back non-null":
    // `Boolean([])` is true, so an empty list would read as an account with no module open.
    const total = modules.length;

    const columns: TableHeaderCellProps[] = [
        { id: 'module', label: c('Account modules').t`Module`, minWidth: 240 },
        { id: 'state', label: c('Account modules').t`State on this account`, minWidth: 260 },
        { id: 'actions', label: '', minWidth: 150, textAlign: 'end' },
    ];

    // The refusal of the read, not of a mutation: answering "you cannot open" to a
    // refused read would name a gesture the operator did not make.
    const readRefusal = () => {
        switch (refusalCode(error)) {
            case 'MISSING_PERMISSION':
                return c('Account modules')
                    .t`Your role does not let you manage accounts, and the modules of an account are read under that permission. They were refused, not answered: this account is not without modules, its modules were not read.`;
            case 'FORBIDDEN_OUT_OF_SCOPE':
                return c('Account modules')
                    .t`This account is outside your portfolio, so its modules were refused rather than answered.`;
            case 'INTERNAL_STAFF_ONLY':
                return c('Account modules')
                    .t`Reading what a client account contains belongs to the back-office, so a client-side role cannot carry it whatever its permissions.`;
            default:
                return c('Account modules')
                    .t`The modules of this account could not be read, so what it opens is unknown — which is not the same as opening nothing.`;
        }
    };

    return (
        <>
            <Card title={c('Account modules').t`Modules of this account`}>
                <Table>
                    <TableHeader resizable cells={columns} />
                    <TableBody loading={loading} loadingRows={3} loadingColumns={columns.length}>
                        {modules.map((module) => {
                            // `enabled` is about this account, `activatable` about the
                            // platform (false everywhere at once). Hence the two cells.
                            const undelivered = !module.activatable;
                            const reasonId = `fl-module-reason-${module.code}`;
                            // ttag refuses an expression inside a localized template, so
                            // the date is formatted before the string that holds it.
                            const grantedOn = formatDate(module.grantedAt);
                            return (
                                <TableRow key={module.code}>
                                    <TableCell>
                                        <span className="font-medium">{module.label}</span>
                                        {/* The technical code, what logs and tickets name it by. */}
                                        <span className="ml-2 font-mono text-xs text-text-500">{module.code}</span>
                                    </TableCell>
                                    <TableCell>
                                        {module.enabled ? (
                                            <span className="flex flex-col gap-1">
                                                <Badge category="success" size="small">
                                                    {c('Account modules').t`Open`}
                                                </Badge>
                                                {/* Since when and by whom, on the row. Rendered
                                                    only once the date is there. */}
                                                {module.grantedAt && (
                                                    <span className="text-xs text-text-600">
                                                        {c('Account modules').t`Since ${grantedOn}`}
                                                        {module.grantedByName ? ` · ${module.grantedByName}` : ''}
                                                    </span>
                                                )}
                                            </span>
                                        ) : (
                                            <Badge category="default" size="small">
                                                {c('Account modules').t`Closed`}
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-end">
                                        {module.enabled ? (
                                            /* Never disabled on `activatable`: a row posted by
                                               hand on an undelivered module must stay erasable. */
                                            <Button
                                                category="weak"
                                                size="small"
                                                onClick={() =>
                                                    setActing({
                                                        module: { code: module.code, label: module.label },
                                                        direction: 'disable',
                                                    })
                                                }
                                            >
                                                {c('Account modules action').t`Close…`}
                                            </Button>
                                        ) : (
                                            <span className="inline-flex items-center justify-end gap-2">
                                                {/* The short qualifier beside the row it
                                                    disables; a control that goes grey without a
                                                    word reads as broken rather than refused. */}
                                                {undelivered && (
                                                    <span id={reasonId} className="text-xs text-text-500">
                                                        {c('Account modules').t`No screen yet`}
                                                    </span>
                                                )}
                                                <Button
                                                    category="weak"
                                                    size="small"
                                                    disabled={undelivered}
                                                    aria-describedby={undelivered ? reasonId : undefined}
                                                    onClick={() =>
                                                        setActing({
                                                            module: { code: module.code, label: module.label },
                                                            direction: 'enable',
                                                        })
                                                    }
                                                >
                                                    {c('Account modules action').t`Open…`}
                                                </Button>
                                            </span>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>

                {/* Three states, told apart. An error rendered as an absence would send
                    the operator away sure the account opens nothing. */}
                {error && (
                    <RegionState
                        tone="error"
                        title={c('Account modules').t`The modules of this account could not be read.`}
                        description={readRefusal()}
                    />
                )}
                {!loading && !error && total === 0 && (
                    <RegionState
                        tone="error"
                        title={c('Account modules').t`The referential of modules came back empty.`}
                        description={c('Account modules')
                            .t`It never is: its rows are seeded by migration and name the entries of the portal menu. So this is the sign of something wrong with the query, not of an account that has nothing — do not read it as « no module exists ».`}
                    />
                )}

                {/* Not an empty list: the rows are all there, all closed. The ordinary
                    state of most accounts, said so it does not read as misconfigured. */}
                {!loading && !error && total > 0 && openCount === 0 && (
                    <p className="mt-4 max-w-3xl text-xs text-text-600" aria-live="polite">
                        {c('Account modules')
                            .t`No module is open on this account. That is normal for an account nobody has set up yet. Opening one makes it appear in its portal users' menu.`}
                    </p>
                )}
                {!loading && !error && total > 0 && openCount > 0 && (
                    <p className="mt-4 max-w-3xl text-xs text-text-600" aria-live="polite">
                        {/* The two numbers are data, never a hard-coded « sur 6 ». */}
                        {ngettext(
                            msgid`${openCount} module is open on this account, out of the ${total} the portal menu names.`,
                            `${openCount} modules are open on this account, out of the ${total} the portal menu names.`,
                            openCount
                        )}
                    </p>
                )}
            </Card>

            {acting && (
                <AccountModuleDialog
                    accountId={accountId}
                    accountName={accountName}
                    module={acting.module}
                    direction={acting.direction}
                    onClose={() => setActing(null)}
                />
            )}
        </>
    );
};

export default AccountModulesPanel;
